import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import { getAiAccessFacilityUsage, getAiAccessUsage } from '../../../../helpers/api';
import { properCase } from '../../../../helpers/utils';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { BRAND, USER_CATEGORIES, cardSx, tableSx, todayInNigeria } from './constants';
import AiAccessPage from './AiAccessPage';

function UsageTab() {
  const [from, setFrom] = useState(todayInNigeria());
  const [to, setTo] = useState(todayInNigeria());
  const [userCategory, setUserCategory] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setRows(await getAiAccessUsage({ from, to, userCategory }));
    } catch (error) {
      setRows([]);
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, userCategory]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = rows.reduce(
    (acc, r) => ({
      assistant: acc.assistant + (r.assistant || 0),
      aiReports: acc.aiReports + (r.aiReports || 0),
      total: acc.total + (r.total || 0),
      users: acc.users.add(r.userId),
    }),
    { assistant: 0, aiReports: 0, total: 0, users: new Set() }
  );

  const tiles = [
    { label: 'Total', value: totals.total },
    { label: 'Ask AI', value: totals.assistant },
    { label: 'AI Report', value: totals.aiReports },
    { label: 'People using AI', value: totals.users.size },
  ];

  return (
    <Box>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Period
        </Typography>

        {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
          Days are in Nigeria time. Pick a range and, if you like, a user category.
        </Typography> */}
        <Stack
          mt={2}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from }}
            sx={{ minWidth: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={userCategory}
              onChange={(e) => setUserCategory(e.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              {USER_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {properCase(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <span style={{ marginLeft: 'auto' }}>
              <IconButton onClick={load} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {loadError ? (
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          {loadError}
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 4,
            }}
          >
            {tiles.map((t) => (
              <Paper key={t.label} elevation={0} sx={{ ...cardSx, px: 2.5, py: 2.25 }}>
                <Typography variant="body2" color="text.secondary">
                  {t.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} color={BRAND} sx={{ mt: 0.75 }}>
                  {loading ? '–' : t.value}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Daily usage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Requests per person per day, busiest first.
          </Typography>

          {loading ? (
            <Paper elevation={0} sx={{ ...cardSx, p: 4 }}>
              <LoaderSkeleton />
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={cardSx}>
              <Table sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Person</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Ask AI</TableCell>
                    <TableCell align="right">AI Report</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ py: '48px !important', color: 'text.secondary' }}
                      >
                        No AI use in this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={`${r.userId}-${r.usageDate}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {r.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{properCase(r.userCategory || '')}</TableCell>
                        <TableCell>
                          {r.usageDate
                            ? new Date(
                                `${String(r.usageDate).slice(0, 10)}T00:00:00`
                              ).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })
                            : ''}
                        </TableCell>
                        <TableCell align="right">{r.assistant ?? 0}</TableCell>
                        <TableCell align="right">{r.aiReports ?? 0}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {r.total ?? 0}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

const formatUsageDate = (value) =>
  value
    ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

// Ask AI questions per facility per day (facility allowances are shared)
function FacilityUsageTab() {
  const [from, setFrom] = useState(todayInNigeria());
  const [to, setTo] = useState(todayInNigeria());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setRows(await getAiAccessFacilityUsage({ from, to }));
    } catch (error) {
      setRows([]);
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const totalQuestions = rows.reduce((sum, r) => sum + (r.questions || 0), 0);
  const facilityCount = new Set(rows.map((r) => r.facilityId)).size;

  return (
    <Box>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Period
        </Typography>
        <br/>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from }}
            sx={{ minWidth: 170 }}
          />
          <Tooltip title="Refresh">
            <span style={{ marginLeft: 'auto' }}>
              <IconButton onClick={load} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {loadError ? (
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          {loadError}
        </Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 4, maxWidth: 520 }}>
            {[
              { label: 'Ask AI questions', value: totalQuestions },
              { label: 'Facilities using Ask AI', value: facilityCount },
            ].map((t) => (
              <Paper key={t.label} elevation={0} sx={{ ...cardSx, px: 2.5, py: 2.25 }}>
                <Typography variant="body2" color="text.secondary">
                  {t.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} color={BRAND} sx={{ mt: 0.75 }}>
                  {loading ? '' : t.value}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Questions per facility
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ask AI questions asked about each facility per day, and by how many people.
          </Typography>

          {loading ? (
            <Paper elevation={0} sx={{ ...cardSx, p: 4 }}>
              <LoaderSkeleton />
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={cardSx}>
              <Table sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Facility</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Questions</TableCell>
                    <TableCell align="right">People</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: '48px !important', color: 'text.secondary' }}>
                        No Ask AI questions in this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={`${r.facilityId}-${r.usageDate}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {r.facilityName}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatUsageDate(r.usageDate)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {r.questions ?? 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{r.users ?? 0}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

// AI Settings > Usage Log
function UsageLog() {
  const [view, setView] = useState('people');

  return (
    <AiAccessPage
      title="AI usage log"
      description="How many Ask AI questions and AI report requests have been made, by person and by facility."
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={view}
          onChange={(_, v) => setView(v)}
          sx={{
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
            '& .MuiTab-root': { minHeight: 48, fontWeight: 700, color: 'text.secondary' },
            '& .Mui-selected': { color: `${BRAND} !important` },
          }}
        >
          <Tab value="people" label="By person" />
          <Tab value="facilities" label="Ask AI by facility" />
        </Tabs>
      </Box>
      {view === 'people' ? <UsageTab /> : <FacilityUsageTab />}
    </AiAccessPage>
  );
}

export default UsageLog;
